import { useQuery } from '@tanstack/react-query'
import { ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useGenesisBondingCurve } from '@/hooks/useBondMarket'
import { useTokens } from '@/hooks/useTokens'
import { formatPrice } from '@/lib/format'
import { fetchGenesisCurveHistory } from '@/lib/api'
import { aggregateGenesisStats } from '@/lib/genesis-stats'
import { NETWORK_NAME } from '@/lib/contracts'
import { IconHome } from '@/ui/icons'
import { MetricRow, PageHeader } from '@/ui/page'
import { ErrorState, Skeleton } from '@/ui/primitives'
import { Reveal, SectionHead } from '@/ui/section'

/**
 * Home — the protocol at a glance.
 *
 * ── What this is for, and how it stays distinct from bCDX ──
 * The app had no front door. `/` redirected to `/cdx`, so the first thing a visitor saw was
 * the token's own page — a price chart and a trade ticket — with no answer to "how big is
 * this thing and is it healthy". The protocol-wide figures existed only as six small rows in
 * the sidebar readout, at 10px, where nobody reads them as a summary.
 *
 * The split, so the two pages never become the same page:
 *
 *   Home  — the PROTOCOL. How much capital is here, where it sits, how fast it arrived.
 *           Cross-product: bonds, lending and collateral in one frame.
 *   bCDX  — the TOKEN. What it costs, what backs it, how to buy it.
 *
 * The reference draws the same line. Its Home is market statistics, Total Value Locked and
 * PBM Analysis — protocol aggregates, each as a stat column beside a chart — while its
 * product pages own the products. This page keeps that shape and drops its repetition: the
 * reference runs two near-identical chart sections back to back, where we have one.
 *
 * ── Why the deposits chart MOVED here from /cdx ──
 * It was the dashboard's "Deposits over time" section. It is a protocol-history figure, not
 * a token figure, and leaving a copy on /cdx while building the same thing here is exactly
 * the duplication this whole pass has been removing. It is here, and only here.
 */
export function Home() {
  const tokens = useTokens()
  const genesis = useGenesisBondingCurve(0n)
  const genesisHistory = useQuery({
    queryKey: ['genesis-curve-history'],
    queryFn: fetchGenesisCurveHistory,
    staleTime: 30_000,
    retry: 1,
  })

  if (genesis.isError) return <ErrorState message="Could not reach the Genesis Bond market." />

  if (genesis.isLoading) {
    return (
      <>
        <PageHeader icon={IconHome} title="Home" />
        <Skeleton className="mb-6 h-[140px] w-full" />
        <Skeleton className="h-[320px] w-full" />
      </>
    )
  }

  const genesisRaised = genesis.curveCollected ?? 0n
  const totals = aggregateGenesisStats(genesis.soldSupply ?? 0n, genesisRaised)
  const totalsAvailable = genesis.soldSupply !== undefined
  const genesisPrice = genesis.currentPrice === undefined
    ? null
    : Number(genesis.currentPrice) / 10 ** tokens.payment.decimals

  return (
    <>
      <PageHeader
        icon={IconHome}
        title="Home"
        subtitle={`Live Genesis Bond market figures from ${NETWORK_NAME}.`}
      />

      <Reveal>
        <section className="mb-6">
          <SectionHead note="live from the Genesis Curve">Protocol overview · Genesis Bond market</SectionHead>
          <MetricRow
            items={[
              {
                label: 'Genesis bCDX price',
                value: genesisPrice === null ? 'Unavailable' : formatPrice(genesisPrice),
                unit: genesisPrice === null ? undefined : tokens.payment.symbol,
                tone: 'enamel',
              },
              {
                label: 'Current bCDX supply',
                value: totalsAvailable ? tokens.cdxCompact(totals.totalSupply) : 'Unavailable',
                unit: totalsAvailable ? tokens.cdx.symbol : undefined,
              },
              {
                label: 'Total bCDX locked',
                value: totalsAvailable ? tokens.cdxCompact(totals.totalLocked) : 'Unavailable',
                unit: totalsAvailable ? tokens.cdx.symbol : undefined,
              },
              {
                label: 'USDT raised',
                value: tokens.payCompact(totals.totalRaised),
                unit: tokens.payment.symbol,
                tone: 'fluid',
              },
              {
                label: 'Remaining to next $1M target',
                value: tokens.payCompact(totals.genesisRemaining),
                unit: tokens.payment.symbol,
              },
              {
                label: 'Genesis Bonds issued',
                value: genesisHistory.isLoading
                  ? 'Loading'
                  : genesisHistory.data?.bondsIssued === null || genesisHistory.data?.bondsIssued === undefined
                    ? 'Unavailable'
                    : String(genesisHistory.data.bondsIssued),
              },
              {
                label: 'Curve status',
                value: genesis.active === undefined ? 'Loading' : genesis.active ? 'Open' : 'Paused',
                note: 'controlled by the on-chain curve owner',
                tone: genesis.active ? 'brass' : 'muted',
              },
            ]}
          />
        </section>
      </Reveal>

      {/*
       * One way out of the summary, rather than a card per product.
       *
       * A grid of "explore Lend / explore Earn" tiles would restate the rail in the content
       * column, which is the duplication we have spent this pass removing. Two text links do
       * the same job at a fraction of the weight.
       */}
      <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 border-t border-line pt-5 text-sm">
        <Link to="/earn/genesis-bond" className="flex items-center gap-1.5 text-accent hover:underline">
          Explore Genesis Bond Market <ArrowRight className="size-3.5" />
        </Link>
      </div>
    </>
  )
}
